import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  Alert,
  Divider
} from '@mui/material';

const PaymentTermView = ({ open, onClose, template }) => {
  if (!template) return null;

  const getPaymentTypeLabel = (type) => {
    const labels = {
      'ADVANCED': 'Advanced',
      'BEFORE_LOADING': 'Before Loading',
      'AGAINST_HBL': 'Against HBL',
      'DAYS_AFTER_HBL_DATE': 'Days After HBL Date',
      'WITHIN_DAYS_AFTER_ETA': 'Within Days After ETA'
    };
    return labels[type] || type;
  };

  const getPaymentTypeDescription = (component) => {
    const { payment_type, days_offset } = component;
    
    switch (payment_type) {
      case 'ADVANCED':
        return 'Payment due immediately';
      case 'BEFORE_LOADING':
        return 'Payment due before loading (ETD)';
      case 'AGAINST_HBL':
        return 'Payment against HBL (on HBL date)';
      case 'DAYS_AFTER_HBL_DATE':
        return `Payment due ${days_offset} days after HBL date`;
      case 'WITHIN_DAYS_AFTER_ETA':
        return `Payment due within ${days_offset} days after ETA`;
      default:
        return '';
    }
  };

  // Calculate total percentage (used only for validation when NOT 100%)
  const totalPercentage = template.components?.reduce((sum, comp) => {
    const pct = parseFloat(comp.percentage) || 0;
    return sum + pct;
  }, 0) || 0;

  const showTotalWarning = Math.abs(totalPercentage - 100) > 0.01;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6" sx={{ lineHeight: 1 }}>{template.abbreviation}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{template.name}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={template.is_active ? 'Active' : 'Inactive'}
              color={template.is_active ? 'success' : 'default'}
              variant={template.is_active ? 'filled' : 'outlined'}
              size="small"
              aria-label={`Status: ${template.is_active ? 'Active' : 'Inactive'}`}
            />
            <Chip
              label={`${template.usage_count || 0} order${template.usage_count === 1 ? '' : 's'}`}
              size="small"
              variant="outlined"
              aria-label="Usage count"
            />
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Payment Structure</Typography>
          {showTotalWarning && (
            <Alert severity={totalPercentage > 100 ? 'error' : 'warning'} sx={{ mb: 2 }}>
              Total percentage: {totalPercentage.toFixed(2)}% — must equal 100%
            </Alert>
          )}
          
          <List>
            {template.components?.map((component, index) => (
              <React.Fragment key={index}>
                <ListItem
                  sx={{
                    py: 1.25,
                    px: 2,
                    borderBottom: '1px solid',
                    borderColor: 'grey.200',
                    '&:last-of-type': { borderBottom: 'none' }
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={`${component.percentage}%`}
                          size="small"
                          color="primary"
                          sx={{ fontWeight: 600 }}
                        />
                        <Typography variant="subtitle2" component="span" sx={{ fontWeight: 600 }}>
                          {getPaymentTypeLabel(component.payment_type)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary" component="span">
                        {getPaymentTypeDescription(component)}
                      </Typography>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              Created {new Date(template.created_at).toLocaleDateString('pt-BR')} • Updated {new Date(template.updated_at).toLocaleDateString('pt-BR')}
              {template.created_by && ` • By ${typeof template.created_by === 'object' ? template.created_by.email || template.created_by.name || 'User' : template.created_by}`}
            </Typography>
          </Box>

          {template.usage_count > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This option is being used by {template.usage_count} order{template.usage_count !== 1 ? 's' : ''} 
              and cannot be deleted. You can deactivate it to prevent new orders from using it.
            </Alert>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentTermView;